/* Iter53 P39 — Enhanced from Iter51 E27.
 *
 * Persistent header badge showing count of imports currently in progress.
 * P39: Click opens a Popover with a live-progress widget per active batch
 *      (using the P36 ImportProgressWidget + /live-progress endpoint).
 *      "Vezi toate" link navigates to /import for full history.
 *
 * Polls `/api/import/batches?status=processing` every 30s (badge count).
 * When popover is open, each ImportProgressWidget polls its own /live-progress
 * at 1.5s cadence — combined bandwidth stays negligible (~350B * N widgets).
 *
 * Visible only when count > 0 (and user is admin/backoffice).
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { RefreshCw, ChevronRight } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ImportProgressWidget } from "./ImportProgressWidget";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const POLL_INTERVAL_MS = 30000;

const ActiveImportsBadge = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);

  const canSee = hasRole(["admin", "backoffice"]);

  const fetchActive = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/import/batches?status=processing&limit=50`);
      setBatches(Array.isArray(r.data) ? r.data : []);
    } catch {
      // Silent — keep last known state
    }
  }, []);

  useEffect(() => {
    if (!canSee) return undefined;
    fetchActive();
    timerRef.current = setInterval(fetchActive, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [canSee, fetchActive]);

  // When a widget signals terminal state, refresh the batches list — the
  // finished batch will drop out on the next poll response.
  const handleBatchFinish = useCallback(() => {
    fetchActive();
  }, [fetchActive]);

  if (!canSee || batches.length === 0) return null;

  const count = batches.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors text-xs font-medium text-blue-700 shadow-sm"
          title="Click pentru a vedea progresul importurilor active"
          data-testid="active-imports-badge"
        >
          <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          <span>
            <strong data-testid="active-imports-count">{count}</strong> import{count !== 1 ? "uri" : ""} activ{count !== 1 ? "e" : ""}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[420px] max-h-[560px] overflow-y-auto p-3"
        data-testid="active-imports-popover"
      >
        <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-100">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Importuri în curs
          </p>
          <button
            type="button"
            onClick={() => { setOpen(false); navigate("/import"); }}
            className="text-xs text-indigo-600 hover:text-indigo-700 font-medium inline-flex items-center gap-0.5"
            data-testid="active-imports-see-all"
          >
            Vezi toate
            <ChevronRight className="h-3 w-3" />
          </button>
        </div>
        <div className="space-y-2">
          {batches.map((b) => (
            <ImportProgressWidget
              key={b.id}
              batchId={b.id}
              onFinish={handleBatchFinish}
              intervalMs={1500}
              compact
              showTitle
              testIdPrefix={`active-imports-widget-${b.id}`}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ActiveImportsBadge;
